// The kart game's sound, all of it made on the spot with WebAudio — no files
// to fetch. Three things make it sit together instead of beeping:
//
//  · One mix. Effects, music and reverb each have a bus, and all of them meet
//    in a compressor before the speakers, so nothing clips and a big hit
//    ducks the music for a moment instead of fighting it.
//  · Layers. A sound is an attack, a body and a tail; the engine is four
//    voices (two saws, a sub, road noise) through a soft clipper; the music
//    is a pad, a bass, drums, an arp and a lead, with the pad, bass and arp
//    pumping against the kick.
//  · Variation. Anything that repeats is varied each time, so it doesn't wear.
//
// A browser only lets a page make sound once it's been touched, so nothing
// plays until unlock() is called from a tap (the start button). Muting is
// remembered on the device. Anything without WebAudio — the tests — gets
// silence, and nothing here ever throws.

const MUTE_KEY = "kartMute";
/** The overall level, before the speakers. */
const LEVEL = 0.6;

let ctx: AudioContext | null = null;
/** Mute and overall level, last before the speakers. */
let out: GainNode | null = null;
/** Where every effect connects. */
let master: GainNode | null = null;
/** Where all the music connects; dips when a hit lands. */
let musicIn: GainNode | null = null;
/** Pad, bass and arp connect here: it ducks on each kick. */
let pump: GainNode | null = null;
/** Sends: reverb (a room to sit the sounds in) and the music's echo. */
let reverbIn: GainNode | null = null;
let echoIn: GainNode | null = null;
let echoDelay: DelayNode | null = null;
let muted = (() => {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
})();

/** A short, dark room: noise that fades out, rolled off so it doesn't hiss. */
function roomImpulse(c: AudioContext, seconds = 1.3) {
  const n = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(2, n, c.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    let lp = 0;
    for (let i = 0; i < n; i++) {
      lp += (Math.random() * 2 - 1 - lp) * 0.3;
      data[i] = lp * (1 - i / n) ** 2.6;
    }
  }
  return buf;
}

/** A soft clip: adds warm harmonics as it gets louder, instead of a hard edge. */
function saturation(amount: number) {
  const n = 1024;
  const curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * amount) / Math.tanh(amount);
  }
  return curve;
}

/** Called from a tap: make (or wake) the sound. */
export function unlock() {
  try {
    if (!ctx && typeof AudioContext !== "undefined") {
      ctx = new AudioContext({ latencyHint: "interactive" });
      // The last stop: a glue compressor, then the level.
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -16;
      comp.knee.value = 14;
      comp.ratio.value = 4;
      comp.attack.value = 0.005;
      comp.release.value = 0.22;
      out = ctx.createGain();
      out.gain.value = muted ? 0 : LEVEL;
      comp.connect(out).connect(ctx.destination);
      master = ctx.createGain();
      master.connect(comp);
      musicIn = ctx.createGain();
      musicIn.gain.value = 0.9;
      musicIn.connect(comp);
      pump = ctx.createGain();
      pump.connect(musicIn);
      // The room.
      const room = ctx.createConvolver();
      room.buffer = roomImpulse(ctx);
      reverbIn = ctx.createGain();
      const wet = ctx.createGain();
      wet.gain.value = 0.55;
      reverbIn.connect(room).connect(wet).connect(comp);
      // The music's echo: a dotted eighth, each repeat a little darker.
      echoIn = ctx.createGain();
      echoDelay = ctx.createDelay(1);
      echoDelay.delayTime.value = 0.32;
      const fb = ctx.createGain();
      fb.gain.value = 0.38;
      const tone = ctx.createBiquadFilter();
      tone.type = "lowpass";
      tone.frequency.value = 2400;
      const echoOut = ctx.createGain();
      echoOut.gain.value = 0.6;
      echoIn.connect(echoDelay);
      echoDelay.connect(tone);
      tone.connect(fb).connect(echoDelay);
      tone.connect(echoOut).connect(musicIn);
      echoOut.connect(reverbIn);
    }
    if (ctx?.state === "suspended") ctx.resume();
    // Music asked for before the first tap starts now.
    if (ctx && pending) {
      const p = pending;
      pending = null;
      music(true, p.faster, p.map);
    }
  } catch {
    ctx = null;
  }
}

export const isMuted = () => muted;

export function setMuted(on: boolean) {
  muted = on;
  if (out && ctx) out.gain.setTargetAtTime(on ? 0 : LEVEL, ctx.currentTime, 0.02);
  try {
    localStorage.setItem(MUTE_KEY, on ? "1" : "0");
  } catch {
    // Not remembered, then.
  }
}

/** Paused: everything stops where it is, and carries on after. */
export function hold(on: boolean) {
  if (!ctx) return;
  if (on) ctx.suspend();
  else ctx.resume();
}

/** The music steps back for a moment, then swells in again: a hit, a sting. */
function duck(depth = 0.45, back = 0.5) {
  if (!ctx || !musicIn) return;
  const t = ctx.currentTime;
  musicIn.gain.cancelScheduledValues(t);
  musicIn.gain.setTargetAtTime(0.9 * depth, t, 0.015);
  musicIn.gain.setTargetAtTime(0.9, t + 0.2, back / 3);
}

/** Sends `from` to the room, `amount` of it. */
function toRoom(from: AudioNode, amount: number) {
  if (!ctx || !reverbIn || amount <= 0) return;
  const send = ctx.createGain();
  send.gain.value = amount;
  from.connect(send).connect(reverbIn);
}

/**
 * A note: a waveform at `freq` (sliding to `to`), `len` seconds long, `delay`
 * from now. It fades in over a few milliseconds, so it never clicks, and `wet`
 * of it goes to the room.
 */
function tone(freq: number, len: number, { type = "triangle" as OscillatorType, to = freq, gain = 0.2, delay = 0, wet = 0.2 } = {}) {
  if (!ctx || !master) return;
  const t = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  osc.frequency.exponentialRampToValueAtTime(to, t + len);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.linearRampToValueAtTime(gain, t + 0.004);
  env.gain.exponentialRampToValueAtTime(0.001, t + len);
  osc.connect(env).connect(master);
  toRoom(env, wet);
  osc.start(t);
  osc.stop(t + len + 0.02);
}

/** The music with its drums taken out: what plays under a race you lost. */
let spare = false;
let noiseBuffer: AudioBuffer | null = null;

/**
 * A voice, roughly: a buzzing saw (the throat) through two band-pass filters
 * (the mouth) that slide from `formants[i][0]` to `[1]` Hz — the vowel opening
 * or closing — with a `growl` Hz flutter in its loudness, the rasp of a voice
 * that's being strained.
 */
function vowel(
  f0: number,
  len: number,
  { to = f0, formants = [[500, 500], [1000, 1000]] as [number, number][], gain = 0.25, delay = 0, growl = 25, wet = 0.15, jitter = 0 } = {},
) {
  if (!ctx || !master) return;
  const t = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  osc.type = "sawtooth";
  // The pitch slides from f0 to `to`, unsteadily by `jitter`: a strained voice never holds a note.
  const steps = Math.max(1, Math.round(len / 0.06));
  osc.frequency.setValueAtTime(f0, t);
  for (let i = 0; i <= steps; i++) osc.frequency.linearRampToValueAtTime(f0 * (to / f0) ** (i / steps) * (1 + (Math.random() - 0.5) * 2 * jitter), t + (len * i) / steps);
  const rasp = ctx.createGain();
  rasp.gain.value = 0.75;
  const lfo = ctx.createOscillator();
  const lfoDepth = ctx.createGain();
  lfo.frequency.value = growl;
  lfoDepth.gain.value = 0.25;
  lfo.connect(lfoDepth).connect(rasp.gain);
  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, t);
  env.gain.linearRampToValueAtTime(gain, t + Math.min(0.04, len * 0.2));
  env.gain.setValueAtTime(gain, t + len * 0.6);
  env.gain.exponentialRampToValueAtTime(0.0001, t + len);
  osc.connect(rasp);
  // Breath through the same mouth, so it's a throat and not a tone.
  const air = ctx.createBufferSource();
  air.buffer = whiteNoise();
  air.loop = true;
  const breath = ctx.createGain();
  breath.gain.value = 0.9;
  air.connect(breath);
  formants.forEach(([a, b], i) => {
    const band = ctx!.createBiquadFilter();
    band.type = "bandpass";
    band.Q.value = 4;
    band.frequency.setValueAtTime(a, t);
    band.frequency.exponentialRampToValueAtTime(b, t + len);
    const level = ctx!.createGain();
    level.gain.value = i ? 0.6 : 1;
    rasp.connect(band).connect(level).connect(env);
    breath.connect(band);
  });
  air.start(t, Math.random() * 0.5);
  air.stop(t + len + 0.05);
  env.connect(master);
  toRoom(env, wet);
  osc.start(t);
  lfo.start(t);
  osc.stop(t + len + 0.05);
  lfo.stop(t + len + 0.05);
}

/** Liquid coming out: noise through a band that closes, its loudness lurching in irregular chunks. */
function wetRush(len: number, delay: number, gain: number) {
  if (!ctx || !master) return;
  const t = ctx.currentTime + delay;
  const src = ctx.createBufferSource();
  src.buffer = whiteNoise();
  const band = ctx.createBiquadFilter();
  band.type = "bandpass";
  band.Q.value = 0.9;
  band.frequency.setValueAtTime(1400, t);
  band.frequency.exponentialRampToValueAtTime(320, t + len);
  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, t);
  const chunks = Math.round(len / 0.05);
  for (let i = 0; i < chunks; i++) {
    const k = i / chunks;
    const shape = Math.min(1, k * 6) * (1 - k * 0.6);
    env.gain.linearRampToValueAtTime(gain * shape * (0.25 + Math.random() * 0.75), t + (len * (i + 1)) / chunks);
  }
  env.gain.linearRampToValueAtTime(0.0001, t + len + 0.1);
  src.connect(band).connect(env).connect(master);
  toRoom(env, 0.1);
  src.start(t, Math.random() * 0.5);
  src.stop(t + len + 0.15);
}

/** A second of white noise, made once. */
function whiteNoise() {
  if (!noiseBuffer && ctx) {
    noiseBuffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = noiseBuffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  }
  return noiseBuffer;
}

/** A burst of noise through a filter sweeping `from` → `to` Hz: a whoosh, a thud, a hat. */
function noise(
  len: number,
  from: number,
  to: number,
  { gain = 0.3, delay = 0, type = "bandpass" as BiquadFilterType, q = 1, attack = 0.2, wet = 0, bus = master as AudioNode | null } = {},
) {
  if (!ctx || !bus || (spare && bus === musicIn)) return;
  const t = ctx.currentTime + delay;
  const src = ctx.createBufferSource();
  const filter = ctx.createBiquadFilter();
  const env = ctx.createGain();
  src.buffer = whiteNoise();
  filter.type = type;
  filter.Q.value = q;
  filter.frequency.setValueAtTime(from, t);
  filter.frequency.exponentialRampToValueAtTime(to, t + len);
  env.gain.setValueAtTime(0.001, t);
  env.gain.exponentialRampToValueAtTime(gain, t + Math.max(0.003, len * attack));
  env.gain.exponentialRampToValueAtTime(0.001, t + len);
  src.connect(filter).connect(env).connect(bus);
  toRoom(env, wet);
  src.start(t, Math.random() * 0.5);
  src.stop(t + len + 0.02);
}

export const sfx = {
  /** 3, 2, 1… a soft bell each; GO, an octave and a fifth up, with a swell under it. */
  count: (go: boolean) => {
    const f = go ? 880 : 440;
    tone(f, go ? 0.7 : 0.25, { type: "sine", gain: go ? 0.2 : 0.2, wet: 0.35 });
    tone(f * 2.01, go ? 0.5 : 0.12, { type: "sine", gain: 0.06, wet: 0.35 });
    if (go) {
      tone(f * 1.5, 0.6, { type: "sine", gain: 0.1, wet: 0.35 });
      noise(0.5, 400, 4000, { gain: 0.1 });
      duck(0.6, 0.6);
    }
  },
  /** The slot spinning, then landing. */
  roulette: (seconds: number) => {
    for (let t = 0; t < seconds; t += 0.07) tone(900 + (t * 600) % 400, 0.035, { type: "sine", gain: 0.07, delay: t, wet: 0.05 });
    tone(1568, 0.4, { type: "sine", gain: 0.15, delay: seconds });
    tone(2093, 0.3, { type: "sine", gain: 0.05, delay: seconds });
  },
  /** A boost: a blow-off of air, a rising whine, a thump under it. */
  boost: (strength = 1) => {
    noise(0.7, 300, 3200, { gain: 0.22 * strength, q: 0.8 });
    tone(180, 0.5, { type: "sawtooth", to: 620, gain: 0.05 * strength, wet: 0.1 });
    tone(90, 0.25, { type: "sine", to: 45, gain: 0.25 * strength, wet: 0 });
  },
  /** A drift's charge reaching a tier: higher each time. */
  charge: (tier: number) => {
    const f = 440 * 2 ** (tier * 0.4);
    tone(f, 0.16, { type: "sine", gain: 0.13, to: f * 1.5, wet: 0.3 });
    tone(f * 2, 0.1, { type: "sine", gain: 0.04, to: f * 3, wet: 0.3 });
  },
  hit: () => {
    noise(0.4, 1400, 100, { gain: 0.5, type: "lowpass", attack: 0.05 });
    tone(150, 0.35, { type: "sine", to: 40, gain: 0.4, wet: 0.1 });
    tone(620, 0.09, { type: "square", to: 200, gain: 0.05 });
    duck(0.4, 0.7);
  },
  trick: () => {
    tone(500, 0.18, { type: "sine", to: 1400, gain: 0.13, wet: 0.35 });
    tone(1000, 0.18, { type: "sine", to: 2800, gain: 0.03, wet: 0.35 });
  },
  /** The last lap: three notes up, the music stepping aside. */
  lastLap: () => {
    duck(0.5, 0.9);
    [523, 659, 784].forEach((f, i) => {
      tone(f, 0.3, { type: "sine", gain: 0.15, delay: i * 0.12, wet: 0.35 });
      tone(f * 2, 0.22, { type: "triangle", gain: 0.05, delay: i * 0.12, wet: 0.35 });
    });
  },
  /** Three of a kind: a run up the scale, once the reels have stopped. */
  jackpot: (delay: number) =>
    [523, 659, 784, 1047, 1319, 1568].forEach((f, i) => {
      tone(f, 0.22, { type: "sine", gain: 0.13, delay: delay + i * 0.07, wet: 0.35 });
      tone(f * 2, 0.12, { type: "sine", gain: 0.04, delay: delay + i * 0.07, wet: 0.35 });
    }),
  /**
   * Beer spilled on the road: a slap as it lands, glugs of liquid rushing out
   * of the can (each a little different), a wet tail. `heavy` (sick, sauce)
   * is lower and thicker.
   */
  spill: (heavy = false) => {
    const p = (heavy ? 0.65 : 1) * (0.9 + Math.random() * 0.25);
    noise(0.09, 2600 * p, 500, { gain: 0.35, type: "highpass" });
    noise(0.7 + Math.random() * 0.2, 1800 * p, 260 * p, { gain: 0.3, type: "bandpass" });
    const glugs = 4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < glugs; i++) {
      const f = (300 + Math.random() * 180) * p * (1 - i * 0.06);
      tone(f, 0.07 + Math.random() * 0.03, { type: "sine", to: f * 1.7, gain: 0.16, delay: 0.05 + i * (0.075 + Math.random() * 0.03), wet: 0.05 });
    }
    noise(0.5, 900 * p, 200, { gain: 0.12, delay: 0.3, type: "lowpass" });
    if (!heavy) tone(2300 * p, 0.05, { type: "triangle", to: 3400 * p, gain: 0.05 });
  },
  /**
   * A man being sick, timed to the head turning (it's round in a fifth of a
   * second): a sharp breath in, two loud "HUURK" heaves climbing in pitch, then
   * the "BLEAAARGH" — a big rough voice falling away over a wet rush with
   * chunks in it — splashes landing on the road behind, and a cough and a gasp
   * to finish. Pitch varies each time.
   */
  barf: () => {
    const v = 0.92 + Math.random() * 0.16;
    // Breath in.
    noise(0.16, 500, 2600, { gain: 0.14, q: 1, attack: 0.9 });
    // HUURK, HUURK: the throat clenching, the pitch jumping up, a thump in the gut under each.
    vowel(95 * v, 0.2, { to: 190 * v, formants: [[520, 720], [1100, 1500]], gain: 0.3, delay: 0.08, growl: 19, jitter: 0.02, wet: 0.1 });
    tone(85, 0.18, { type: "sine", to: 50, gain: 0.25, delay: 0.1, wet: 0 });
    vowel(88 * v, 0.24, { to: 210 * v, formants: [[540, 780], [1150, 1600]], gain: 0.36, delay: 0.31, growl: 21, jitter: 0.02, wet: 0.1 });
    tone(80, 0.2, { type: "sine", to: 45, gain: 0.3, delay: 0.33, wet: 0 });
    // BLEAAARGH: a big open voice sliding down, doubled an octave below, over the wet rush.
    const at = 0.56;
    vowel(230 * v, 0.9, { to: 92 * v, formants: [[620, 900], [1350, 1100]], gain: 0.42, delay: at, growl: 16, jitter: 0.03, wet: 0.2 });
    vowel(115 * v, 0.9, { to: 46 * v, formants: [[500, 700], [1000, 900]], gain: 0.16, delay: at, growl: 13, jitter: 0.03, wet: 0.1 });
    wetRush(0.95, at - 0.02, 0.5);
    noise(0.8, 260, 110, { gain: 0.32, delay: at + 0.05, type: "lowpass" });
    for (let i = 0; i < 12; i++) {
      const f = (150 + Math.random() * 190) * v;
      tone(f, 0.05 + Math.random() * 0.04, { type: "sine", to: f * 1.9, gain: 0.1, delay: at + 0.1 + i * (0.05 + Math.random() * 0.04), wet: 0.05 });
    }
    // It hits the road: a big slap, then splashes.
    noise(0.18, 1800, 250, { gain: 0.4, delay: at + 0.25, type: "lowpass", attack: 0.04 });
    tone(110, 0.22, { type: "sine", to: 50, gain: 0.3, delay: at + 0.25, wet: 0 });
    for (let i = 0; i < 4; i++) noise(0.09 + Math.random() * 0.06, 1500 + Math.random() * 800, 300, { gain: 0.2, delay: at + 0.5 + i * (0.09 + Math.random() * 0.06), type: "lowpass", attack: 0.05 });
    // A cough, a spit, a gasp.
    vowel(125 * v, 0.13, { to: 175 * v, formants: [[560, 700], [1200, 1400]], gain: 0.24, delay: at + 1, growl: 20, jitter: 0.02 });
    noise(0.05, 3200, 1500, { gain: 0.1, delay: at + 1.16, q: 2, attack: 0.1 });
    noise(0.3, 1500, 900, { gain: 0.08, delay: at + 1.22, q: 2, attack: 0.5 });
    duck(0.45, 1);
  },
  /** Across the line: a major chord opening up over a low root if you did well, two soft falling notes if you didn't. The music plays on. */
  finish: (win = true) => {
    if (win) {
      duck(0.55, 0.9);
      [523, 659, 784, 1047].forEach((f, i) => {
        tone(f, i === 3 ? 1.4 : 0.4, { type: "triangle", gain: 0.16, delay: i * 0.13, wet: 0.4 });
        tone(f * 2, i === 3 ? 1 : 0.2, { type: "sine", gain: 0.04, delay: i * 0.13, wet: 0.4 });
      });
      tone(131, 1.4, { type: "sine", gain: 0.25, delay: 0.4, wet: 0.1 });
    } else {
      duck(0.6, 0.8);
      [392, 330].forEach((f, i) => tone(f, i ? 0.9 : 0.35, { type: "triangle", to: f * 0.97, gain: 0.14, delay: i * 0.28, wet: 0.4 }));
      tone(98, 0.9, { type: "sine", gain: 0.2, delay: 0.28, wet: 0.1 });
    }
  },
};

// ── The engine ───────────────────────────────────────────────────────────────

interface Engine {
  saws: OscillatorNode[];
  sub: OscillatorNode;
  filter: BiquadFilterNode;
  gain: GainNode;
  road: BiquadFilterNode;
  roadGain: GainNode;
  squeal: BiquadFilterNode;
  squealGain: GainNode;
  whine: OscillatorNode;
  whineGain: GainNode;
}
let engine: Engine | null = null;

/**
 * A soft engine: a triangle and a faint saw a few cents apart (they beat, like
 * a real engine's cylinders never quite agree), a triangle an octave down for body,
 * all through a soft clipper and a filter that opens with the revs; a slow
 * wobble in the pitch so it idles like something alive. Under it, road noise;
 * over it, a tyre squeal for the drift and a turbine whine for the boost.
 */
function makeEngine(c: AudioContext, bus: AudioNode): Engine {
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.Q.value = 0.7;
  filter.frequency.value = 260;
  const shaper = c.createWaveShaper();
  shaper.curve = saturation(1.2);
  shaper.oversample = "2x";
  const gain = c.createGain();
  gain.gain.value = 0;
  filter.connect(shaper).connect(gain).connect(bus);

  const wobble = c.createOscillator();
  const wobbleDepth = c.createGain();
  wobble.frequency.value = 3.1;
  wobbleDepth.gain.value = 0.6;
  wobble.connect(wobbleDepth);
  wobble.start();

  const saws = [-5, 6].map((cents, i) => {
    const osc = c.createOscillator();
    osc.type = i ? "sawtooth" : "triangle";
    osc.detune.value = cents;
    const level = c.createGain();
    level.gain.value = i ? 0.12 : 0.4;
    osc.connect(level).connect(filter);
    wobbleDepth.connect(osc.frequency);
    osc.start();
    return osc;
  });
  const sub = c.createOscillator();
  sub.type = "triangle";
  const subLevel = c.createGain();
  subLevel.gain.value = 0.6;
  sub.connect(subLevel).connect(filter);
  sub.start();

  // The road and the wind.
  const roadSrc = c.createBufferSource();
  roadSrc.buffer = whiteNoise();
  roadSrc.loop = true;
  const road = c.createBiquadFilter();
  road.type = "bandpass";
  road.Q.value = 0.6;
  road.frequency.value = 500;
  const roadGain = c.createGain();
  roadGain.gain.value = 0;
  roadSrc.connect(road).connect(roadGain).connect(bus);
  roadSrc.start();

  // Tyres: narrow, high noise, wavering.
  const squealSrc = c.createBufferSource();
  squealSrc.buffer = whiteNoise();
  squealSrc.loop = true;
  const squeal = c.createBiquadFilter();
  squeal.type = "bandpass";
  squeal.Q.value = 9;
  squeal.frequency.value = 2100;
  const squealGain = c.createGain();
  squealGain.gain.value = 0;
  squealSrc.connect(squeal).connect(squealGain).connect(bus);
  squealSrc.start();
  const shake = c.createOscillator();
  const shakeDepth = c.createGain();
  shake.frequency.value = 11;
  shakeDepth.gain.value = 140;
  shake.connect(shakeDepth).connect(squeal.frequency);
  shake.start();

  // The boost's turbine.
  const whine = c.createOscillator();
  whine.type = "sine";
  whine.frequency.value = 800;
  const whineGain = c.createGain();
  whineGain.gain.value = 0;
  whine.connect(whineGain).connect(bus);
  whine.start();

  return { saws, sub, filter, gain, road, roadGain, squeal, squealGain, whine, whineGain };
}

/**
 * The engine, following `speed` (0…1 of top speed, more on a boost); `on`
 * false fades it out. `drift` squeals the tyres, `boost` spins up the turbine.
 */
export function engineAt(speed: number, on: boolean, { drift = false, boost = false } = {}) {
  if (!ctx || !master) return;
  engine ||= makeEngine(ctx, master);
  const e = engine;
  const t = ctx.currentTime;
  const s = Math.min(1.5, Math.max(0, speed));
  // Revs: an idle at 44 Hz, 150 at full speed and more past it. The rise is
  // eased (a heavier engine takes a moment to spin up), the fall a little quicker.
  const rev = 44 + s * 70 + s * s * 30;
  e.saws.forEach((o) => o.frequency.setTargetAtTime(rev, t, 0.09));
  e.sub.frequency.setTargetAtTime(rev / 2, t, 0.09);
  e.filter.frequency.setTargetAtTime(260 + s * 480, t, 0.12);
  e.gain.gain.setTargetAtTime(on ? 0.06 + Math.min(1, s) * 0.03 : 0, t, on ? 0.12 : 0.25);
  e.road.frequency.setTargetAtTime(350 + s * 1400, t, 0.12);
  e.roadGain.gain.setTargetAtTime(on ? Math.min(1, s) ** 1.5 * 0.03 : 0, t, 0.15);
  e.squeal.frequency.setTargetAtTime(1900 + s * 500, t, 0.1);
  e.squealGain.gain.setTargetAtTime(on && drift && s > 0.3 ? 0.016 : 0, t, drift ? 0.06 : 0.12);
  e.whine.frequency.setTargetAtTime(700 + s * 700, t, 0.15);
  e.whineGain.gain.setTargetAtTime(on && boost ? 0.007 : 0, t, boost ? 0.08 : 0.2);
}

// ── The music ────────────────────────────────────────────────────────────────
//
// A song for each map, all on the same scheduler (sixteen steps to a bar,
// eight bars to a loop) and the same little kit. The last lap lifts the tempo
// and steps the key up two semitones — the oldest trick there is.
//
//  · Vittsjö — Swedish pop, in the ABBA manner: A major at 102, a rolling
//    piano, a singing bass, strings, a tambourine, a hook.
//  · Rebæk Søpark — 2000s EDM: E minor at 136, a hard kick, an offbeat bass,
//    a pumping supersaw pad, a trance pluck, a hook on supersaws, and a
//    riser and snare roll into every loop.
//  · Val T — après-ski: G major at 126, an oompah bass, accordion on the
//    offbeats, sleigh bells, claps, a hook you can sing on the way down.

let beat: ReturnType<typeof setInterval> | null = null;
let tempo = 1;
/** Semitones the song is up by: 2 on the last lap. */
let lifted = 0;
let nextAt = 0;
let stepNo = 0;

/** What a song is told on each sixteenth. */
interface Step {
  n: number;
  /** The sixteenth within the bar, 0…15, and the bar within the loop, 0…7. */
  at: number;
  bar: number;
  /** Seconds from now that it falls, and one sixteenth's length. */
  delay: number;
  len: number;
  /** Semitones to lift the whole song by (the last lap). */
  lift: number;
  /** This bar's root, in semitones from A2 (110 Hz), with the lift, and its chord's intervals. */
  root: number;
  chord: number[];
}
interface Song {
  bpm: number;
  roots: number[];
  chords: number[][];
  step: (s: Step) => void;
}

const MAJ = [0, 4, 7];
const MIN = [0, 3, 7];
/** A pitch `semis` from `base` Hz. */
const hz = (semis: number, base = 110) => base * 2 ** (semis / 12);
/** A melody's notes: [step, semitones from A4, length in steps]. */
type Melody = [number, number, number][];

/**
 * One music voice: `voices` oscillators spread `detune` cents either side of
 * the note, through a filter that opens to `cutoff` and closes to `cutTo`,
 * fading in over `attack` and out over `release`, with `echo` and `verb` of it
 * sent on.
 */
function voice(
  freq: number,
  len: number,
  bus: AudioNode,
  { type = "sawtooth" as OscillatorType, detune = 0, voices = 2, gain = 0.1, cutoff = 2000, cutTo = 500, attack = 0.005, release = len, delay = 0, echo = 0, verb = 0, q = 1 } = {},
) {
  if (!ctx) return;
  const t = ctx.currentTime + delay;
  const filter = ctx.createBiquadFilter();
  const env = ctx.createGain();
  filter.type = "lowpass";
  filter.Q.value = q;
  filter.frequency.setValueAtTime(cutoff, t);
  filter.frequency.exponentialRampToValueAtTime(Math.max(60, cutTo), t + len);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.linearRampToValueAtTime(gain, t + attack);
  env.gain.setValueAtTime(gain, Math.max(t + attack, t + len - release));
  env.gain.exponentialRampToValueAtTime(0.0001, t + len);
  filter.connect(env).connect(bus);
  if (echo > 0 && echoIn) {
    const send = ctx.createGain();
    send.gain.value = echo;
    env.connect(send).connect(echoIn);
  }
  toRoom(env, verb);
  const spread = detune && voices > 1 ? Array.from({ length: voices }, (_, i) => (i / (voices - 1) * 2 - 1) * detune) : [0];
  for (const cents of spread) {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;
    osc.detune.value = cents;
    osc.connect(filter);
    osc.start(t);
    osc.stop(t + len + 0.05);
  }
}

/** The kick: a sine falling out of a click, and the pump against it (`duck`: how far everything else dips, 1 for not at all). */
function kick(delay: number, { level = 0.5, from = 140, to = 46, duck = 0.35, click = 0.06 } = {}) {
  if (!ctx || !musicIn || !pump || spare) return;
  const t = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.frequency.setValueAtTime(from, t);
  osc.frequency.exponentialRampToValueAtTime(to, t + 0.14);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.linearRampToValueAtTime(level, t + 0.004);
  env.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
  osc.connect(env).connect(musicIn);
  osc.start(t);
  osc.stop(t + 0.35);
  noise(0.02, 3500, 600, { gain: click, delay, attack: 0.1, bus: musicIn });
  pump.gain.cancelScheduledValues(t);
  pump.gain.setValueAtTime(duck, t);
  pump.gain.linearRampToValueAtTime(1, t + 0.2);
}

/** A clap: a few quick noise bursts and a tail, in the room. */
function clap(delay: number, level = 0.1) {
  if (!musicIn) return;
  const o = { type: "bandpass" as const, q: 1.1, attack: 0.05, bus: musicIn };
  [0, 0.011, 0.023].forEach((d) => noise(0.02, 1500, 1300, { ...o, gain: level * 0.9, delay: delay + d }));
  noise(0.16, 1700, 1000, { ...o, gain: level, delay: delay + 0.03, wet: 0.5 });
}

/** A snare: a body and a rattle. */
function snare(delay: number, level = 0.1, wet = 0.2) {
  if (!ctx || !musicIn || spare) return;
  const t = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(230, t);
  osc.frequency.exponentialRampToValueAtTime(140, t + 0.1);
  env.gain.setValueAtTime(0.0001, t);
  env.gain.linearRampToValueAtTime(level * 0.9, t + 0.003);
  env.gain.exponentialRampToValueAtTime(0.001, t + 0.13);
  osc.connect(env).connect(musicIn);
  osc.start(t);
  osc.stop(t + 0.16);
  noise(0.16, 2600, 1500, { gain: level, delay, q: 0.7, attack: 0.03, bus: musicIn, wet });
}

/** A hat, open (`len` long) or closed: band-passed, so it shimmers and doesn't bite. */
function hat(delay: number, len: number, gain: number, freq = 7500) {
  if (musicIn) noise(len, freq, freq * 0.8, { gain, delay, type: "bandpass", q: 0.9, bus: musicIn, attack: 0.1 });
}

/** Draws a melody's notes that fall on this step. */
function sing(notes: Melody, s: Step, make: (freq: number, len: number) => void) {
  for (const [at, semis, len] of notes) if (at === s.at) make(hz(semis + s.lift, 440), s.len * len * 0.95);
}

// Vittsjö ────────────────────────────────────────────────────────────────────

/** A I–V–vi–IV song: A E F#m D · A E D E. */
const POP_LEAD: Melody[] = [
  [[0, 4, 2], [2, 4, 2], [4, 7, 3], [8, 9, 2], [10, 7, 2], [12, 4, 4]],
  [[0, 2, 2], [2, 4, 2], [4, 2, 3], [8, -1, 2], [10, 2, 2], [12, 4, 4]],
  [[0, 4, 2], [2, 4, 2], [4, 9, 3], [8, 7, 2], [10, 4, 2], [12, 0, 4]],
  [[0, 5, 2], [2, 5, 2], [4, 9, 3], [8, 7, 2], [10, 5, 2], [12, 2, 4]],
  [[0, 4, 2], [2, 4, 2], [4, 7, 3], [8, 9, 2], [10, 12, 2], [12, 9, 4]],
  [[0, 11, 2], [2, 9, 2], [4, 7, 3], [8, 4, 2], [10, 7, 2], [12, 2, 4]],
  [[0, 5, 2], [2, 9, 2], [4, 12, 3], [8, 9, 2], [10, 5, 2], [12, 9, 4]],
  [[0, 7, 2], [2, 11, 2], [4, 14, 6], [12, 11, 2], [14, 7, 2]],
];
/** The bass sings: [step, semitones above the root, length]. */
const POP_BASS: [number, number, number][] = [[0, 0, 3], [3, 0, 1], [4, 12, 2], [6, 7, 2], [8, 0, 3], [11, 0, 1], [12, 12, 2], [14, 7, 2]];

const vittsjo: Song = {
  bpm: 102,
  roots: [0, -5, -3, 5, 0, -5, 5, -5],
  chords: [MAJ, MAJ, MIN, MAJ, MAJ, MAJ, MAJ, MAJ],
  step(s) {
    const { at, bar, delay, len, root, chord } = s;
    // A soft disco: kick on one and three, a snare on two and four, eighth hats, an open one off the beat, a tambourine.
    if (at === 0 || at === 8) kick(delay, { level: 0.34, from: 120, to: 50, duck: 0.8, click: 0.03 });
    if (at === 4 || at === 12) snare(delay, 0.08);
    if (at % 2 === 0) hat(delay, 0.04, 0.02, 9000);
    if (at % 4 === 2) hat(delay, 0.1, 0.035, 8000);
    if (at % 2 === 1) noise(0.05, 5600, 5000, { gain: 0.018, delay, type: "bandpass", q: 2, bus: musicIn });
    // Strings: the chord swelling in and out over the bar, two voices each, wide.
    if (at === 0) {
      for (const c of chord) voice(hz(root + c, 220), len * 16 * 1.05, pump!, { detune: 10, gain: 0.026, cutoff: 1500, cutTo: 2200, attack: len * 5, release: len * 6, delay, verb: 0.4 });
    }
    // Piano: rolling eighths up and down the chord, bright and short.
    if (at % 2 === 0) {
      const k = (at / 2) % 4;
      const f = hz(root + chord[[0, 1, 2, 1][k]], k === 2 ? 440 : 330);
      voice(f, len * 3.5, pump!, { type: "triangle", gain: 0.06, cutoff: 4800, cutTo: 900, attack: 0.003, delay, verb: 0.2, echo: 0.12 });
      voice(f * 2, len * 1.5, pump!, { type: "sine", gain: 0.014, cutoff: 6000, cutTo: 2000, attack: 0.002, delay });
    }
    for (const [b, up, l] of POP_BASS) {
      if (b !== at) continue;
      const f = hz(root + up, 110);
      voice(f, len * l, pump!, { type: "triangle", gain: 0.2, cutoff: 900, cutTo: 350, attack: 0.008, release: len * l * 0.5, delay });
      voice(f, len * l, pump!, { type: "sawtooth", gain: 0.03, cutoff: 700, cutTo: 300, attack: 0.008, release: len * l * 0.5, delay });
    }
    // The hook, after the first four bars: a soft sweet lead, an octave shadowing it.
    if (s.n >= 64) {
      sing(POP_LEAD[bar], s, (f, l) => {
        voice(f, l, musicIn!, { type: "triangle", detune: 6, gain: 0.05, cutoff: 3600, cutTo: 2400, attack: 0.02, release: l * 0.5, delay, echo: 0.25, verb: 0.35 });
        voice(f * 2, l, musicIn!, { type: "sine", gain: 0.012, cutoff: 5000, cutTo: 3000, attack: 0.03, release: l * 0.5, delay, verb: 0.35 });
      });
    }
  },
};

// Rebæk Søpark ───────────────────────────────────────────────────────────────

/** Em C G D, the hook two bars long and played twice. */
const EDM_LEAD: Melody[] = [
  [[0, 14, 3], [4, 12, 2], [6, 10, 2], [8, 14, 3], [12, 12, 2], [14, 10, 2]],
  [[0, 12, 3], [4, 10, 2], [6, 7, 2], [8, 12, 3], [12, 10, 2], [14, 7, 2]],
  [[0, 14, 3], [4, 10, 2], [6, 14, 2], [8, 17, 4], [14, 14, 2]],
  [[0, 14, 3], [4, 9, 2], [6, 14, 2], [8, 12, 6]],
];

const rebaek: Song = {
  bpm: 136,
  roots: [-5, 3, -2, 5, -5, 3, -2, 5],
  chords: [MIN, MAJ, MAJ, MAJ, MIN, MAJ, MAJ, MAJ],
  step(s) {
    const { at, bar, delay, len, root, chord } = s;
    const build = bar === 7;
    // Four on the floor, hard; a clap and a snare on two and four; open hats off the beat, sixteenths between.
    if (at % 4 === 0) kick(delay, { level: 0.55, from: 160, to: 45, duck: 0.22, click: 0.08 });
    if (at === 4 || at === 12) {
      clap(delay, 0.11);
      snare(delay, 0.05, 0.1);
    }
    if (at % 4 === 2) hat(delay, 0.1, 0.06, 8000);
    else if (at % 2 === 1) hat(delay, 0.035, 0.022, 7000);
    // Offbeat bass, sitting in the gaps the kick leaves.
    if (at % 4 === 2) {
      const f = hz(root, 110);
      voice(f, len * 1.6, pump!, { type: "sawtooth", gain: 0.11, cutoff: 1000, cutTo: 300, attack: 0.004, release: len, delay, q: 3 });
      voice(f, len * 1.6, pump!, { type: "sine", gain: 0.2, cutoff: 500, cutTo: 300, attack: 0.005, release: len, delay });
    }
    // The pad: a supersaw chord, made to breathe by the kick.
    if (at === 0) {
      for (const c of chord) voice(hz(root + c, 220), len * 16 * 1.05, pump!, { voices: 5, detune: 16, gain: 0.011, cutoff: 3000, cutTo: 1200, attack: 0.02, release: len * 3, delay, verb: 0.25 });
    }
    // The trance pluck: sixteenths through the chord, a note in each.
    {
      const idx = [0, 1, 2, 1, 2, 1, 0, 2, 0, 1, 2, 1, 2, 1, 2, 1][at];
      voice(hz(root + chord[idx], 440), len * 1.5, pump!, { gain: 0.022, cutoff: 3800, cutTo: 700, attack: 0.002, delay, echo: 0.4, q: 3 });
    }
    // The hook on supersaws, from the fifth bar; a riser and a snare roll into the loop.
    if (s.n >= 64) {
      sing(EDM_LEAD[bar % 4], s, (f, l) => voice(f, l, musicIn!, { voices: 3, detune: 12, gain: 0.03, cutoff: 3500, cutTo: 2200, attack: 0.008, release: l * 0.4, delay, echo: 0.4, verb: 0.3 }));
    }
    if (build && at === 0) noise(len * 16, 500, 9000, { gain: 0.07, type: "highpass", attack: 0.95, bus: musicIn, wet: 0.2, delay });
    if (build && at >= 8) snare(delay, 0.03 + (at - 8) * 0.012, 0.15);
  },
};

// Val T ──────────────────────────────────────────────────────────────────────

/** G C D G · G Em C D: a chorus to shout. */
const SKI_LEAD: Melody[] = [
  [[0, 14, 2], [2, 14, 2], [4, 12, 2], [6, 10, 2], [8, 14, 4], [12, 10, 4]],
  [[0, 12, 2], [2, 12, 2], [4, 10, 2], [6, 7, 2], [8, 12, 4], [12, 7, 4]],
  [[0, 9, 2], [2, 9, 2], [4, 12, 2], [6, 14, 2], [8, 17, 6], [14, 14, 2]],
  [[0, 14, 3], [4, 10, 3], [8, 14, 3], [12, 10, 2], [14, 14, 2]],
  [[0, 14, 2], [2, 14, 2], [4, 12, 2], [6, 10, 2], [8, 14, 4], [12, 17, 4]],
  [[0, 14, 2], [2, 14, 2], [4, 12, 2], [6, 7, 2], [8, 14, 4], [12, 7, 4]],
  [[0, 12, 2], [2, 12, 2], [4, 10, 2], [6, 7, 2], [8, 12, 4], [12, 7, 4]],
  [[0, 9, 2], [2, 12, 2], [4, 14, 2], [6, 17, 2], [8, 14, 8]],
];

const valT: Song = {
  bpm: 126,
  roots: [-2, 3, 5, -2, -2, -5, 3, 5],
  chords: [MAJ, MAJ, MAJ, MAJ, MAJ, MIN, MAJ, MAJ],
  step(s) {
    const { at, bar, delay, len, root, chord } = s;
    // A stomping four on the floor, claps on two and four, sleigh bells on every eighth.
    if (at % 4 === 0) kick(delay, { level: 0.42, from: 130, to: 48, duck: 0.6, click: 0.05 });
    if (at === 4 || at === 12) clap(delay, 0.13);
    if (at % 2 === 0) {
      noise(0.07, 6500, 5200, { gain: at % 4 === 2 ? 0.04 : 0.026, delay, type: "bandpass", q: 3, bus: musicIn });
      const f = 3000 + Math.random() * 900;
      voice(f, 0.09, musicIn!, { type: "sine", gain: at % 4 === 2 ? 0.014 : 0.008, cutoff: 9000, cutTo: 6000, attack: 0.002, delay, verb: 0.25 });
    }
    // Oompah: the tuba on one and three, root then fifth, and the accordion on the offbeats.
    if (at === 0 || at === 8) {
      const f = hz(root + (at === 8 ? 7 : 0), 110);
      voice(f, len * 3, pump!, { type: "triangle", gain: 0.22, cutoff: 700, cutTo: 350, attack: 0.008, release: len * 1.5, delay });
      voice(f, len * 3, pump!, { type: "sawtooth", gain: 0.025, cutoff: 600, cutTo: 300, attack: 0.008, release: len * 1.5, delay });
    }
    if (at % 4 === 2) {
      for (const c of chord) voice(hz(root + c, 220), len * 2.4, pump!, { type: "sawtooth", detune: 11, gain: 0.03, cutoff: 2600, cutTo: 1100, attack: 0.008, release: len * 1.4, delay, verb: 0.15 });
    }
    // The chorus, after two bars: a reedy square, doubled, with a room round it.
    if (s.n >= 32) {
      sing(SKI_LEAD[bar], s, (f, l) => voice(f, l, musicIn!, { type: "square", detune: 9, gain: 0.03, cutoff: 3000, cutTo: 1800, attack: 0.012, release: l * 0.4, delay, echo: 0.22, verb: 0.3 }));
    }
  },
};

// The menu ─────────────────────────────────────────────────────────────────

/**
 * Made to be left on: no drums, no tune to catch on. A slow pad through
 * Dmaj7 · Bm7 · Gmaj7 · Asus4, a low note under every other bar, and a few
 * soft bell notes drawn at random from the chord — never the same twice,
 * never more than a few to a bar, and never in a hurry.
 */
const menu: Song = {
  bpm: 66,
  roots: [5, 2, -2, 0, 5, 2, -2, 0],
  chords: [[0, 4, 7, 11], [0, 3, 7, 10], [0, 4, 7, 11], [0, 5, 7, 10], [0, 4, 7, 11], [0, 3, 7, 10], [0, 4, 7, 11], [0, 5, 7, 10]],
  step(s) {
    const { at, bar, delay, len, root, chord } = s;
    if (at === 0) {
      for (const c of chord) voice(hz(root + c, 220), len * 16 * 1.1, pump!, { type: "triangle", detune: 7, gain: 0.02, cutoff: 1100, cutTo: 1500, attack: len * 6, release: len * 8, delay, verb: 0.5 });
      if (bar % 2 === 0) {
        const f = hz(root, 55);
        voice(f, len * 14, pump!, { type: "sine", gain: 0.11, cutoff: 500, cutTo: 300, attack: len * 2, release: len * 8, delay });
        voice(f * 2, len * 14, pump!, { type: "triangle", gain: 0.018, cutoff: 600, cutTo: 300, attack: len * 2, release: len * 8, delay });
      }
    }
    // A bell now and then, from the second bar: a chord tone, sometimes an octave up, quiet and long in the room.
    if (s.n >= 24 && at % 2 === 0 && Math.random() < 0.2) {
      const f = hz(root + chord[Math.floor(Math.random() * chord.length)] + (Math.random() < 0.35 ? 12 : 0), 440);
      voice(f, 2.4, musicIn!, { type: "sine", gain: 0.05, cutoff: 3000, cutTo: 900, attack: 0.006, release: 2.4, delay, echo: 0.55, verb: 0.55 });
      voice(f * 2.005, 0.7, musicIn!, { type: "sine", gain: 0.008, cutoff: 6000, cutTo: 2000, attack: 0.003, release: 0.7, delay, verb: 0.4 });
    }
  },
};

/** Which song each map plays, by the map's key; the menu has its own. */
const SONGS: Record<string, Song> = { forest: vittsjo, meadow: rebaek, snow: valT, menu };
let song = rebaek;

/** The scheduler's one step: hand the song this sixteenth. */
function musicStep(n: number, delay: number) {
  if (!ctx || !musicIn || !pump) return;
  const bar = Math.floor(n / 16) % 8;
  const lift = lifted;
  const len = 60 / song.bpm / 4 / tempo;
  if (n % 16 === 0 && echoDelay) echoDelay.delayTime.setTargetAtTime((60 / song.bpm / tempo) * 0.75, ctx.currentTime, 0.1);
  song.step({ n, at: n % 16, bar, delay, len, lift, root: song.roots[bar] + lift, chord: song.chords[bar] });
}

/** Music asked for before the first tap, when the browser won't yet allow it. */
let pending: { faster: number; map: string } | null = null;

/**
 * The music: on (a map's key picks the song, "menu" the menu's), off, and how
 * much quicker than usual (the last lap). Asked for while another song plays,
 * it changes over; asked for the same song again, it only changes the tempo.
 */
export function music(on: boolean, faster = 1, map = "") {
  tempo = faster;
  lifted = faster > 1 ? 2 : 0;
  spare = false;
  if (!on) {
    pending = null;
    if (beat) clearInterval(beat);
    beat = null;
    return;
  }
  if (!ctx) {
    pending = { faster, map };
    return;
  }
  const next = SONGS[map] ?? rebaek;
  if (beat) {
    if (next !== song) {
      song = next;
      stepNo = 0;
      nextAt = ctx.currentTime + 0.08;
    }
    return;
  }
  song = next;
  nextAt = ctx.currentTime + 0.08;
  stepNo = 0;
  // A step scheduler: every 25 ms, queue whatever falls in the next 100 ms.
  beat = setInterval(() => {
    if (!ctx) return;
    while (nextAt < ctx.currentTime + 0.1) {
      musicStep(stepNo, Math.max(0, nextAt - ctx.currentTime));
      nextAt += 60 / song.bpm / 4 / tempo;
      stepNo++;
    }
  }, 25);
}

/**
 * The race is over: the song carries on. Winning, it settles to its own tempo
 * and stays in the lifted key, full band; losing, it slows a little, drops
 * back to its own key, and loses the drums.
 */
export function outro(win: boolean) {
  tempo = win ? 1 : 0.88;
  lifted = win ? 2 : 0;
  spare = !win;
}

/** Everything off: leaving the game. `keepMusic` leaves the song playing. */
export function silence(keepMusic = false) {
  if (!keepMusic) music(false);
  engineAt(0, false);
  stopAmbience();
}

// ── The weapons' own sounds ──────────────────────────────────────────────────

/** A voice that keeps running while it's near enough to hear: its level, 0…1, is set every frame. */
interface Voice {
  gain: GainNode;
  stop: () => void;
}
const voices: Record<string, Voice | undefined> = {};

function loopingNoise() {
  if (!ctx) return null;
  const src = ctx.createBufferSource();
  src.buffer = whiteNoise();
  src.loop = true;
  return src;
}

/** The smoke machine: a hiss through a steam-pipe filter, puffing about twice a second. */
function smokeVoice(): Voice | null {
  const src = loopingNoise();
  if (!ctx || !master || !src) return null;
  const band = ctx.createBiquadFilter();
  band.type = "bandpass";
  band.frequency.value = 3800;
  band.Q.value = 0.5;
  const high = ctx.createBiquadFilter();
  high.type = "highpass";
  high.frequency.value = 1400;
  // The puffs: a slow wave on the hiss's loudness.
  const puff = ctx.createGain();
  puff.gain.value = 0.6;
  const lfo = ctx.createOscillator();
  const depth = ctx.createGain();
  lfo.frequency.value = 2.1;
  depth.gain.value = 0.4;
  lfo.connect(depth).connect(puff.gain);
  const gain = ctx.createGain();
  gain.gain.value = 0;
  src.connect(band).connect(high).connect(puff).connect(gain).connect(master);
  src.start();
  lfo.start();
  return { gain, stop: () => [src, lfo].forEach((n) => n.stop()) };
}

/** Mosquitoes: two thin whines a little apart, wavering, beating against each other. */
function mosquitoVoice(): Voice | null {
  if (!ctx || !master) return null;
  const gain = ctx.createGain();
  gain.gain.value = 0;
  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 1100;
  filter.Q.value = 0.7;
  filter.connect(gain).connect(master);
  const stops: (() => void)[] = [];
  [430, 447, 512].forEach((f, i) => {
    const osc = ctx!.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = f;
    // Each wavers round its note, at its own speed.
    const lfo = ctx!.createOscillator();
    const swing = ctx!.createGain();
    lfo.frequency.value = 5 + i * 1.7;
    swing.gain.value = 22;
    lfo.connect(swing).connect(osc.frequency);
    const level = ctx!.createGain();
    level.gain.value = 0.35;
    osc.connect(level).connect(filter);
    osc.start();
    lfo.start();
    stops.push(() => {
      osc.stop();
      lfo.stop();
    });
  });
  return { gain, stop: () => stops.forEach((f) => f()) };
}

/** The Soundboks's techno: four on the floor at 140, offbeat hats, a clap, an acid line — its own bus, so its level can follow the distance. */
let techno: { bus: GainNode; timer: ReturnType<typeof setInterval>; stop: () => void } | null = null;
const ACID = [0, 0, 12, 0, 3, 0, 7, 0, 0, 12, 0, 10, 0, 5, 0, 3];
function technoVoice() {
  if (!ctx || !master) return null;
  const bus = ctx.createGain();
  bus.gain.value = 0;
  bus.connect(master);
  let at = ctx.currentTime + 0.05;
  let n = 0;
  const hit = (freq: number, len: number, o: { type?: OscillatorType; to?: number; gain?: number; delay?: number; cutoff?: number }) => {
    const t = ctx!.currentTime + (o.delay ?? 0);
    const osc = ctx!.createOscillator();
    const env = ctx!.createGain();
    osc.type = o.type ?? "sine";
    osc.frequency.setValueAtTime(freq, t);
    osc.frequency.exponentialRampToValueAtTime(o.to ?? freq, t + len);
    env.gain.setValueAtTime(o.gain ?? 0.2, t);
    env.gain.exponentialRampToValueAtTime(0.001, t + len);
    let out: AudioNode = osc;
    if (o.cutoff) {
      const f = ctx!.createBiquadFilter();
      f.type = "lowpass";
      f.Q.value = 9;
      f.frequency.setValueAtTime(o.cutoff * 2.5, t);
      f.frequency.exponentialRampToValueAtTime(o.cutoff * 0.4, t + len);
      osc.connect(f);
      out = f;
    }
    out.connect(env).connect(bus);
    osc.start(t);
    osc.stop(t + len + 0.02);
  };
  const hat = (delay: number, len: number, gain: number) => {
    const src = ctx!.createBufferSource();
    src.buffer = whiteNoise();
    const f = ctx!.createBiquadFilter();
    f.type = "highpass";
    f.frequency.value = 7000;
    const env = ctx!.createGain();
    const t = ctx!.currentTime + delay;
    env.gain.setValueAtTime(gain, t);
    env.gain.exponentialRampToValueAtTime(0.001, t + len);
    src.connect(f).connect(env).connect(bus);
    src.start(t);
    src.stop(t + len + 0.02);
  };
  const timer = setInterval(() => {
    if (!ctx) return;
    while (at < ctx.currentTime + 0.1) {
      const delay = Math.max(0, at - ctx.currentTime);
      const step16 = n % 16;
      if (step16 % 4 === 0) {
        // The kick: a thump falling from a click.
        hit(180, 0.22, { to: 42, gain: 0.55, delay });
        hit(3000, 0.012, { type: "square", to: 800, gain: 0.08, delay });
      }
      if (step16 % 4 === 2) hat(delay, 0.09, 0.12);
      else if (step16 % 2 === 1) hat(delay, 0.03, 0.05);
      if (step16 === 4 || step16 === 12) hat(delay, 0.16, 0.16);
      const note = ACID[step16];
      // A line that squelches: a saw through a resonant filter that closes on each note.
      hit(55 * 2 ** (note / 12) * (step16 % 8 === 5 ? 2 : 1), 0.13, { type: "sawtooth", gain: 0.13, delay, cutoff: 700 + (n % 32) * 40 });
      at += 60 / 140 / 4;
      n++;
    }
  }, 25);
  return { bus, timer, stop: () => clearInterval(timer) };
}

/**
 * The weapons' sounds that go on as long as they're near: 0 (out of earshot)
 * to 1 (right on top of you), each frame. The smoke machine and the mosquitoes
 * loop; the techno plays its own beat while the Soundboks is on.
 */
export function ambience(level: { smoke: number; mosquito: number; techno: number }) {
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  for (const [name, make] of [["smoke", smokeVoice], ["mosquito", mosquitoVoice]] as const) {
    const want = level[name];
    let v = voices[name];
    if (!v && want > 0.01) v = voices[name] = make() ?? undefined;
    v?.gain.gain.setTargetAtTime(want * (name === "smoke" ? 0.16 : 0.08), t, 0.08);
  }
  if (!techno && level.techno > 0.01) techno = technoVoice();
  techno?.bus.gain.setTargetAtTime(level.techno * 0.8, t, 0.08);
  if (techno && level.techno <= 0.01 && techno.bus.gain.value < 0.005) {
    techno.stop();
    techno.bus.disconnect();
    techno = null;
  }
}

/** Everything the ambience runs, stopped. */
function stopAmbience() {
  for (const name of Object.keys(voices)) {
    voices[name]?.stop();
    voices[name]?.gain.disconnect();
    voices[name] = undefined;
  }
  techno?.stop();
  techno?.bus.disconnect();
  techno = null;
}
